// @flow

import React, {Component} from 'react';
import {Field} from 'redux-form';
import BasicFormInput from '../BasicFormInput/BasicFormInput';
import DecimalFormInput from '../DecimalFormInput/DecimalFormInput';
import SelectFormInput from '../SelectFormInput/SelectFormInput';
import ActionConfirmation from '../../containers/ActionConfirmation/ActionConfirmation';
import _ from 'lodash/fp';
import AdvancedProductSlotDialog from '../AdvancedProductSlotDialog/AdvancedProductSlotDialog';
import ReactTooltip from 'react-tooltip';
import DisableContent from '../DisableContent/DisableContent';
import './ProductSlotForm.css';
import type {
  ModifiedState,
  NavigationState,
} from '../../reducers/configTemplateView';
import type {
  ErrorDto,
  ProductDto,
  ProductSlotDto,
  ProductTypeDto,
  RoomDto,
} from '../../dtos';

const productSlotTypes = [{
  value: true,
  label: 'Avancerad',
}, {
  value: false,
  label: 'Enkel',
}];

const notArchived =
  _.filter({archived: null});

const includeProductType = productSlot =>
  _.filter(t => t.id === productSlot.productTypeId || !t.archived);

const filterProductTypes = productSlot =>
  productSlot
    ? includeProductType(productSlot)
    : notArchived;

const getRootName = (formValues, productSlot, productTypes) => {
  const productType = !!formValues.productTypeId
    ? _.find({id: formValues.productTypeId}, productTypes)
    : null;
  const rootName = !!formValues.name
    ? `Produktplats ${formValues.name}`
    : 'Produktplats';
  return !!productType
    ? `${rootName} (${productType.name})`
    : rootName;
};

export type Props = {
  productSlot: ?ProductSlotDto,
  room: ?RoomDto,
  formValues: any,
  productTypes: ProductTypeDto[],
  products: ProductDto[],
  onArchive: () => any,
  onUserChange: () => any,
  change: Function,
  openDialog: Function,
  closeDialog: Function,
  dialogError: ?ErrorDto,
  dialogIsFetching: boolean,
  showDialog: boolean,
  navigateToVariant: Function,
  popDialogNavigation: Function,
  dialogNavigation: ?NavigationState,
  updateRelatedVariant: Function,
  toggleVariant: Function,
  setStandardProduct: Function,
  modifiedData: ModifiedState,
  toggleProduct: Function,
  sharedChecked: ?boolean,
  checkAllVariants: Function,
  clearStandardProduct: Function,
  isDirty: boolean,
  checkProduct: Function,
  updateVariants: Function,
  updateProductSlots: (sortDirection: 'up' | 'down') => void,
  sortDownDisabled: boolean,
  sortUpDisabled: boolean,
  updateVariantsForType: Function,
};

class ProductSlotForm extends Component<Props> {
  props: Props;

  onProductTypeIdChange(value: ?number) {
    const {
      formValues,
      change,
    } = this.props;

    if (value !== formValues.productTypeId) {
      change('blacklistedProducts', []);
      change('defaultProductId', null);
    }
  }

  getSlotProducts() {
    const {
      products,
      formValues,
      room,
    } = this.props;

    if (!formValues || !formValues.productTypeId) {
      return [];
    }

    const roomProducts = !!room
      ? _.filter(p =>
        !p.roomTypes.length || _.some({id: room.roomTypeId}, p.roomTypes), products)
      : products;

    return _.filter({productTypeId: formValues.productTypeId}, roomProducts);
  }

  getCurrentDefaultProduct() {
    const {
      productSlot,
      products,
    } = this.props;

    return !!productSlot
      ? _.find({id: productSlot.defaultProductId}, products)
      : null;
  }

  getDefaultProductOptions() {
    const options = this.getSlotProducts();
    const current = this.getCurrentDefaultProduct();

    return current ? _.unionBy('id', options, [current]) : options;
  }

  getCurrentBlacklistProducts() {
    const {
      productSlot,
      products,
    } = this.props;

    return !!productSlot
      ? _.filter(p =>
        _.some({id: p.id}, productSlot.blacklistedProducts), products)
      : products;
  }

  getBlacklistOptions() {
    const options = this.getSlotProducts();
    const current = this.getCurrentBlacklistProducts();

    return _.unionBy('id', options, current);
  }

  render() {
    const {
      productSlot,
      room,
      productTypes,
      onArchive,
      onUserChange,
      formValues,
      openDialog,
      closeDialog,
      dialogIsFetching,
      dialogError,
      showDialog,
      navigateToVariant,
      popDialogNavigation,
      dialogNavigation,
      updateRelatedVariant,
      toggleVariant,
      setStandardProduct,
      modifiedData,
      toggleProduct,
      sharedChecked,
      checkAllVariants,
      clearStandardProduct,
      isDirty,
      checkProduct,
      updateVariants,
      updateProductSlots,
      sortDownDisabled,
      sortUpDisabled,
      updateVariantsForType,
    } = this.props;

    return (
      <div className="ProductSlotForm">
        <div className="row align-justify align-middle box-header">
          <div className="column">
            <span className="box-title">
              {!!room ? room.name : ''} - {(productSlot && productSlot.name) || 'Ny produktplats'}
            </span>
          </div>
          <div className="column shrink horizontal-button-list">
            {productSlot && productSlot.id &&
              <DisableContent disabled={isDirty}>
                {[
                  <button
                    key={0}
                    type="button"
                    className="button secondary small"
                    disabled={sortUpDisabled}
                    onClick={() => updateProductSlots('up')}>
                    <i className="fa fa-arrow-up"/>
                  </button>,
                  <button
                    key={1}
                    type="button"
                    className="button secondary small"
                    disabled={sortDownDisabled}
                    onClick={() => updateProductSlots('down')}>
                    <i className="fa fa-arrow-down"/>
                  </button>,
                  <ActionConfirmation
                    key={2}
                    id="archiveProductSlot"
                    className="secondary small"
                    message={`Vill du verkligen ${productSlot.archived ? 'av' : ''}arkivera den här produktplatsen?`}
                    buttonText={productSlot.archived ? 'Avarkivera' : 'Arkivera'}
                    action={onArchive} />,
                ]}
              </DisableContent>
            }
          </div>
        </div>
        <Field
          name="name"
          label="Namn"
          onChange={onUserChange}
          component={BasicFormInput}
          type="text" />
        <Field
          name="productTypeId"
          label="Produkttyp"
          disabled={!!(productSlot && productSlot.id)}
          normalize={v => {
            const value = v ? v.id : null;
            this.onProductTypeIdChange(value);
            return value;
          }}
          onChange={onUserChange}
          component={SelectFormInput}
          valueKey="id"
          labelKey="name"
          options={filterProductTypes(productSlot)(productTypes)} />
        {formValues && formValues.productTypeId &&
          <Field
            name="advanced"
            label="Typ av produktplats"
            normalize={x => x ? x.value : false}
            onChange={onUserChange}
            component={SelectFormInput}
            valueKey="value"
            labelKey="label"
            options={productSlotTypes} />
        }
        {formValues && formValues.productTypeId && !formValues.advanced &&
          <div>
            <Field
              name="defaultProductId"
              label="Standardprodukt"
              normalize={value => value ? value.id : null}
              onChange={onUserChange}
              component={SelectFormInput}
              valueKey="id"
              labelKey="name"
              options={this.getDefaultProductOptions()} />
            <Field
              name="unit"
              label="Enhet"
              onChange={onUserChange}
              component={BasicFormInput}
              type="text" />
            <Field
              name="quantity"
              label="Antal"
              onChange={onUserChange}
              component={DecimalFormInput} />
            <Field
              name="blacklistedProducts"
              label="Otillåtna produkter"
              onChange={onUserChange}
              component={SelectFormInput}
              valueKey="id"
              labelKey="name"
              options={this.getBlacklistOptions()}
              multi={true} />
          </div>
        }
        {formValues && formValues.productTypeId && !!formValues.advanced &&
          <div
            data-tip
            data-for="advanced-tip">
            <button
              type="button"
              className="button small expanded"
              disabled={!productSlot || !productSlot.id}
              onClick={() => openDialog(formValues.productTypeId, getRootName(formValues, productSlot, productTypes))}>
              Redigera avancerad produktplats
            </button>
            {(!productSlot || !productSlot.id) && (
              <ReactTooltip
                id="advanced-tip"
                effect="solid">
                <span>Produktplatsen måste sparas innan den kan konfigureras</span>
              </ReactTooltip>
            )}
            {!!showDialog && !!dialogNavigation &&
              <AdvancedProductSlotDialog
                isFetching={dialogIsFetching}
                error={dialogError}
                onClose={closeDialog}
                navigateToVariant={navigateToVariant}
                popDialogNavigation={popDialogNavigation}
                navigationStack={dialogNavigation}
                updateRelatedVariant={(variant, data) => {
                  onUserChange();
                  updateRelatedVariant(variant, data);
                }}
                toggleVariant={toggleVariant}
                setStandardProduct={(product, productType) => {
                  onUserChange();
                  setStandardProduct(product, productType);
                }}
                modifiedData={modifiedData}
                toggleProduct={toggleProduct}
                sharedChecked={sharedChecked}
                checkAllVariants={checked => {
                  onUserChange();
                  checkAllVariants(checked);
                }}
                clearStandardProduct={productType => {
                  onUserChange();
                  clearStandardProduct(productType);
                }}
                checkProduct={(productId, checked) => {
                  onUserChange();
                  checkProduct(productId, checked);
                }}
                updateVariants={(productId, data) => {
                  onUserChange();
                  updateVariants(productId, data);
                }}
                updateVariantsForType={(productTypeId, data) => {
                  onUserChange();
                  updateVariantsForType(productTypeId, data);
                }} />
            }
          </div>
        }
      </div>
    );
  }
}

export default ProductSlotForm;



// WEBPACK FOOTER //
// ./src/components/ProductSlotForm/ProductSlotForm.js